/** Keeps `#dk-theme-vars` the last of OUR stylesheets in <head>.
 *
 *  That node carries the generated theme variables, then the template's
 *  theme_css, then the seller's custom_css (App.vue applyTheme), and it wins the
 *  cascade by coming last. A lazy section's chunk CSS is appended to <head> when
 *  the chunk loads — after it — so at equal specificity the section's own rules
 *  would beat the theme's and the seller's overrides. Since CS3 of
 *  docs/plans/storefront-design-quality.md moved every section's rules into its
 *  chunk, that is the normal case, not an edge.
 *
 *  Only the bundle's own stylesheets count: the `<link href="/assets/…css">`
 *  Vite's preload helper appends, and its dev server's `<style data-vite-dev-id>`.
 *  A seller's head snippet or a vendor widget's styles keep the order they have
 *  today.
 *
 *  A MutationObserver callback runs as a microtask, before the next frame, so
 *  the wrong order is never painted. Observes <head>'s own children only. It sits
 *  in the entry chunk, so it is written small. */

export const isBundleStylesheet = (n: Node): boolean =>
  n instanceof HTMLLinkElement
    ? n.rel === "stylesheet" && /^\/assets\/[^/]+\.css/.test(n.getAttribute("href") as string)
    : // dev only, so a production build drops the branch
      import.meta.env.DEV && n instanceof HTMLStyleElement && n.hasAttribute("data-vite-dev-id")

/** Starts the observer; returns its disconnect. */
export function keepThemeLast(): () => void {
  const mo = new MutationObserver((records) => {
    const theme = document.getElementById("dk-theme-vars")
    // 4 = Node.DOCUMENT_POSITION_FOLLOWING: the new sheet landed after the theme.
    if (theme && records.some((r) => [...r.addedNodes].some((n) => isBundleStylesheet(n) && theme.compareDocumentPosition(n) & 4)))
      document.head.appendChild(theme)
  })
  mo.observe(document.head, { childList: true })
  return () => mo.disconnect()
}
